import React, { useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import {
  Page,
  LegacyCard,
  IndexTable,
  Text,
  Badge,
  Spinner,
  BlockStack,
  Modal,
  Divider,
  TextField,
  InlineStack,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { useNavigate } from "react-router-dom";
import { formatFriendlyDate, formatPlate } from "../../utility/funcs";

function PendingReports() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default: sort by date (column index 3), ascending (oldest first)
  const [sortColumnIndex, setSortColumnIndex] = useState(3);
  const [sortDirection, setSortDirection] = useState("ascending");
  const [search, setSearch] = useState("");

  const [detailItem, setDetailItem] = useState(null);
  const [detailSignedUrl, setDetailSignedUrl] = useState(null);
  const [saving, setSaving] = useState(false);

  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const handlePrint = () => {
    const groupByCar = sortColumnIndex === 0;
    const sortLabels = {
      0: "Auto",
      1: "Reporte",
      2: "Staff",
      3: "Fecha",
      4: "Foto",
    };
    const sortDirLabel = sortDirection === "ascending" ? "asc" : "desc";
    const filterLabel = search.trim() ? `Filtro: "${search.trim()}"` : "Sin filtro";
    const sortLabel = `Orden: ${sortLabels[sortColumnIndex] || "Fecha"} (${sortDirLabel})`;

    const today = new Date().toLocaleDateString("es-MX", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    let bodyRows = "";
    let lastCarId = null;
    for (const r of sortedRows) {
      const carId = r.checklist?.car?.id ?? "_unknown";
      const plate = r.checklist?.car?.plate || "—";
      const model =
        r.checklist?.car?.model?.show_as ||
        r.checklist?.car?.model?.name ||
        "";
      if (groupByCar && carId !== lastCarId) {
        const groupCount = sortedRows.filter(
          (x) => (x.checklist?.car?.id ?? "_unknown") === carId
        ).length;
        bodyRows += `<tr class="group"><td colspan="5"><strong>${escapeHtml(plate)}</strong> ${escapeHtml(model)} — ${groupCount} reporte${groupCount === 1 ? "" : "s"}</td></tr>`;
        lastCarId = carId;
      }
      bodyRows += `<tr>
        <td>${groupByCar ? "" : `<strong>${escapeHtml(plate)}</strong><br/><span class="muted">${escapeHtml(model)}</span>`}</td>
        <td>${r.item_question ? `<span class="muted">${escapeHtml(r.item_question)}</span><br/>` : ""}<strong>${escapeHtml(r.item_title)}</strong></td>
        <td>${escapeHtml(r.checklist?.staff?.name || "-")}</td>
        <td>${escapeHtml(formatFriendlyDate(r.checklist?.created_at || r.created_at))}</td>
        <td>${r.photo_url ? "Sí" : "No"}</td>
      </tr>`;
    }

    const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Reportes Pendientes — ${escapeHtml(today)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #111; margin: 24px; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .meta { font-size: 11px; color: #555; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  thead th { background: #f4f4f4; text-align: left; padding: 6px 8px; border-bottom: 2px solid #999; font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; }
  tbody td { padding: 6px 8px; border-bottom: 1px solid #ddd; vertical-align: top; }
  tr.group td { background: #fafafa; padding: 8px; border-top: 2px solid #999; border-bottom: 1px solid #999; }
  .muted { color: #777; font-size: 10px; }
  .footer { margin-top: 24px; font-size: 10px; color: #777; text-align: right; }
  @media print {
    body { margin: 12mm; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
  }
</style>
</head>
<body>
<h1>Reportes Pendientes</h1>
<div class="meta">${escapeHtml(today)} · ${escapeHtml(sortedRows.length)} reporte${sortedRows.length === 1 ? "" : "s"} · ${escapeHtml(sortLabel)} · ${escapeHtml(filterLabel)}</div>
<table>
  <thead>
    <tr>
      <th>Auto</th>
      <th>Reporte</th>
      <th>Staff</th>
      <th>Fecha</th>
      <th>Foto</th>
    </tr>
  </thead>
  <tbody>
    ${bodyRows || `<tr><td colspan="5" style="text-align:center;padding:24px;color:#777">Sin reportes</td></tr>`}
  </tbody>
</table>
<div class="footer">GoMove · generado ${escapeHtml(new Date().toLocaleString("es-MX"))}</div>
</body>
</html>`;

    // Print via a hidden iframe so we don't open a new tab.
    const existing = document.getElementById("__print_frame");
    if (existing) existing.remove();
    const iframe = document.createElement("iframe");
    iframe.id = "__print_frame";
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        console.error(e);
      }
    };
  };

  const fetchData = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("car_checklist_items")
      .select(
        "id, item_title, item_question, answer, photo_url, created_at, dealt_with, checklist:car_checklists(id, created_at, car:cars(id, plate, model:car_models(name, show_as)), staff:users!staff_id(name))"
      )
      .eq("answer", "negative")
      .or("dealt_with.is.null,dealt_with.eq.false");
    setRows(data || []);
    setLoading(false);
  };

  // Client-side filter + sort — keeps UI snappy without refetching
  const sortedRows = React.useMemo(() => {
    // Filter by search (car plate/model OR report title/question)
    const q = search.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) => {
          const plate = (r.checklist?.car?.plate || "").toLowerCase();
          const model = (
            r.checklist?.car?.model?.show_as ||
            r.checklist?.car?.model?.name ||
            ""
          ).toLowerCase();
          const title = (r.item_title || "").toLowerCase();
          const question = (r.item_question || "").toLowerCase();
          return (
            plate.includes(q) ||
            model.includes(q) ||
            title.includes(q) ||
            question.includes(q)
          );
        })
      : rows;

    const arr = [...filtered];
    const dir = sortDirection === "descending" ? -1 : 1;
    const keyFor = {
      0: (r) => r.checklist?.car?.plate || "",           // Auto
      1: (r) => r.item_title || "",                      // Reporte
      2: (r) => r.checklist?.staff?.name || "",          // Staff
      3: (r) => r.checklist?.created_at || r.created_at || "", // Fecha
      4: (r) => (r.photo_url ? "1" : "0"),               // Foto
    };
    const getKey = keyFor[sortColumnIndex] || keyFor[3];
    arr.sort((a, b) => {
      const cmp = getKey(a).localeCompare(getKey(b));
      if (cmp !== 0) return cmp * dir;
      // Tiebreaker: oldest first
      const ad = a.checklist?.created_at || a.created_at || "";
      const bd = b.checklist?.created_at || b.created_at || "";
      return ad.localeCompare(bd);
    });
    return arr;
  }, [rows, sortColumnIndex, sortDirection, search]);

  const handleSort = (index, direction) => {
    setSortColumnIndex(index);
    setSortDirection(direction);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openDetail = async (row) => {
    setDetailItem(row);
    setDetailSignedUrl(null);
    if (row.photo_url) {
      const { data } = await supabase.storage
        .from("checklist-photos")
        .createSignedUrl(row.photo_url, 3600);
      setDetailSignedUrl(data?.signedUrl || null);
    }
  };

  const markAsDealt = async () => {
    if (!detailItem) return;
    setSaving(true);
    await supabase
      .from("car_checklist_items")
      .update({ dealt_with: true })
      .eq("id", detailItem.id);
    setSaving(false);
    setDetailItem(null);
    // Refresh list
    fetchData();
  };

  // Group by car to count reports per car
  const perCarCount = rows.reduce((acc, r) => {
    const cid = r.checklist?.car?.id;
    if (!cid) return acc;
    acc[cid] = (acc[cid] || 0) + 1;
    return acc;
  }, {});

  return (
    <AppWrapper>
      <Page
        title="Reportes Pendientes"
        subtitle={
          rows.length > 0
            ? `${rows.length} reporte(s) sin resolver en ${Object.keys(perCarCount).length} auto(s)`
            : "Sin reportes pendientes"
        }
        primaryAction={
          rows.length > 0
            ? { content: "Imprimir", onAction: handlePrint }
            : undefined
        }
      >
        <BlockStack gap="400">
          {loading ? (
            <Spinner size="small" />
          ) : rows.length === 0 ? (
            <LegacyCard sectioned>
              <Text variant="bodyMd" tone="subdued">
                No hay reportes pendientes — todos los autos están al día.
              </Text>
            </LegacyCard>
          ) : (
            <>
            <TextField
              placeholder="Filtrar por auto o reporte"
              value={search}
              onChange={setSearch}
              clearButton
              onClearButtonClick={() => setSearch("")}
              autoComplete="off"
            />
            <LegacyCard>
              <IndexTable
                resourceName={{ singular: "Reporte", plural: "Reportes" }}
                selectable={false}
                itemCount={sortedRows.length}
                sortable={[true, true, true, true, true]}
                sortColumnIndex={sortColumnIndex}
                sortDirection={sortDirection}
                onSort={handleSort}
                headings={[
                  { title: "Auto" },
                  { title: "Reporte" },
                  { title: "Staff" },
                  { title: "Fecha" },
                  { title: "Foto" },
                ]}
              >
                {(() => {
                  // When sorting by the "Auto" column, group rows under a
                  // subheader per car (plate + model + count). Otherwise render flat.
                  const groupByCar = sortColumnIndex === 0;
                  const elements = [];
                  let position = 0;
                  let currentCarId = null;

                  const renderReport = (r) => (
                    <IndexTable.Row
                      id={String(r.id)}
                      key={r.id}
                      position={position++}
                      onClick={() => openDetail(r)}
                    >
                      <IndexTable.Cell>
                        {groupByCar ? null : (
                          <>
                            <Text variant="bodySm" fontWeight="semibold">
                              {r.checklist?.car?.plate || "-"}
                            </Text>
                            <Text variant="bodySm" tone="subdued">
                              {r.checklist?.car?.model?.show_as ||
                                r.checklist?.car?.model?.name ||
                                ""}
                            </Text>
                          </>
                        )}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <div
                          style={{
                            width: 360,
                            maxWidth: 360,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {r.item_question && (
                            <Text variant="bodySm" tone="subdued">
                              {r.item_question}
                            </Text>
                          )}
                          <Text variant="bodySm" fontWeight="semibold">
                            {r.item_title}
                          </Text>
                        </div>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text variant="bodySm">
                          {r.checklist?.staff?.name || "-"}
                        </Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text variant="bodySm">
                          {formatFriendlyDate(
                            r.checklist?.created_at || r.created_at
                          )}
                        </Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {r.photo_url ? (
                          <Badge tone="info">Sí</Badge>
                        ) : (
                          <Badge>No</Badge>
                        )}
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  );

                  for (const r of sortedRows) {
                    if (groupByCar) {
                      const carId = r.checklist?.car?.id ?? "_unknown";
                      if (carId !== currentCarId) {
                        currentCarId = carId;
                        const groupCount = sortedRows.filter(
                          (x) => (x.checklist?.car?.id ?? "_unknown") === carId
                        ).length;
                        const plate = r.checklist?.car?.plate || "—";
                        const model =
                          r.checklist?.car?.model?.show_as ||
                          r.checklist?.car?.model?.name ||
                          "";
                        elements.push(
                          <IndexTable.Row
                            id={`group-${carId}`}
                            key={`group-${carId}`}
                            rowType="subheader"
                            position={position++}
                            selectionRange={[0, 0]}
                          >
                            <IndexTable.Cell colSpan={5}>
                              <InlineStack gap="200" blockAlign="center">
                                <Text variant="bodyMd" fontWeight="semibold">
                                  {plate}
                                </Text>
                                {model && (
                                  <Text variant="bodySm" tone="subdued">
                                    {model}
                                  </Text>
                                )}
                                <Badge>
                                  {`${groupCount} ${groupCount === 1 ? "reporte" : "reportes"}`}
                                </Badge>
                              </InlineStack>
                            </IndexTable.Cell>
                          </IndexTable.Row>
                        );
                      }
                    }
                    elements.push(renderReport(r));
                  }
                  return elements;
                })()}
              </IndexTable>
            </LegacyCard>
            </>
          )}
        </BlockStack>

        <Modal
          open={!!detailItem}
          onClose={() => setDetailItem(null)}
          title="Reporte Pendiente"
          primaryAction={{
            content: "Marcar como atendido",
            onAction: markAsDealt,
            loading: saving,
            disabled: saving,
          }}
          secondaryActions={[
            {
              content: "Ver checklist completo",
              onAction: () => {
                if (detailItem?.checklist?.id) {
                  navigate(`/checklists/${detailItem.checklist.id}`);
                }
              },
            },
            { content: "Cerrar", onAction: () => setDetailItem(null) },
          ]}
        >
          {detailItem && (
            <Modal.Section>
              <BlockStack gap="300">
                <div>
                  <Text variant="bodySm" tone="subdued">Auto</Text>
                  <Text
                    variant="bodyMd"
                    fontWeight="semibold"
                    onClick={() =>
                      detailItem.checklist?.car?.id &&
                      navigate(`/cars/${detailItem.checklist.car.id}`)
                    }
                    style={{ cursor: detailItem.checklist?.car?.id ? "pointer" : "default" }}
                  >
                    {formatPlate(detailItem.checklist?.car?.plate)} —{" "}
                    {detailItem.checklist?.car?.model?.show_as ||
                      detailItem.checklist?.car?.model?.name ||
                      ""}
                  </Text>
                </div>
                <Divider />
                <div>
                  {detailItem.item_question && (
                    <Text variant="bodySm" tone="subdued">
                      {detailItem.item_question}
                    </Text>
                  )}
                  <Text variant="headingMd">{detailItem.item_title}</Text>
                  <div style={{ marginTop: 4 }}>
                    <Badge tone="critical">Falla</Badge>
                  </div>
                </div>
                {detailSignedUrl && (
                  <div>
                    <Text variant="bodySm" tone="subdued">Foto</Text>
                    <a
                      href={detailSignedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: "inline-block", marginTop: 4 }}
                    >
                      <img
                        src={detailSignedUrl}
                        alt={detailItem.item_title}
                        style={{
                          maxWidth: "100%",
                          maxHeight: 320,
                          borderRadius: 6,
                          objectFit: "contain",
                          border: "1px solid var(--p-color-border)",
                        }}
                      />
                    </a>
                  </div>
                )}
                {detailItem.photo_url && !detailSignedUrl && (
                  <Text variant="bodySm" tone="subdued">Cargando imagen…</Text>
                )}
                <div>
                  <Text variant="bodySm" tone="subdued">Staff</Text>
                  <Text variant="bodyMd">
                    {detailItem.checklist?.staff?.name || "-"}
                  </Text>
                </div>
                <div>
                  <Text variant="bodySm" tone="subdued">Fecha del checklist</Text>
                  <Text variant="bodyMd">
                    {formatFriendlyDate(detailItem.checklist?.created_at)}
                  </Text>
                </div>
              </BlockStack>
            </Modal.Section>
          )}
        </Modal>
      </Page>
    </AppWrapper>
  );
}

export default PendingReports;
